<script setup>
/* global axios */
import { ref, computed, onMounted, watch } from 'vue';

const props = defineProps({
  initialValues: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['change']);

const procedures = ref([]);
const digitalHumans = ref([]);
const physicians = ref([]);
const loading = ref(false);
const error = ref(null);

const form = ref({
  // Sistema
  procedure_id: '',
  assigned_physician_id: '',
  assigned_digital_human_id: '',
  surgery_date: '',
  journey_phase: 'pre_op',
  medical_record_number: '',

  // Identificação
  birth_date: '',
  gender: '',

  // Antropometria + alergias
  height_cm: '',
  weight_kg: '',
  blood_type: '',
  allergies: [],

  // Equipe médica
  physician_ids: [],
  last_consultation: '',

  // Diagnóstico
  primary_diagnosis: '',

  // Listas dinâmicas
  previous_diseases: [],
  current_medications: [],
  other_exams: [],

  // Exames
  exam_results: '',

  // Acompanhamento
  program_name: '',
  subcategory_name: '',
  care_template_name: '',
  care_description: '',
  notes: '',
  medical_guidance: '',
  ...props.initialValues,
});

// Inputs temporários pras listas
const newDisease = ref('');
const newMedication = ref('');
const newExam = ref('');
const newAllergy = ref('');

// IMC calculado de altura+peso (não armazenado — derivado, igual a idade).
const bmi = computed(() => {
  const h = parseFloat(form.value.height_cm);
  const w = parseFloat(form.value.weight_kg);
  if (!h || !w || h <= 0) return null;
  const m = h / 100;
  return Math.round((w / (m * m)) * 10) / 10;
});

function buildAccountUrl(path) {
  const match = window.location.pathname.match(/accounts\/(\d+)/);
  const id = match ? match[1] : 1;
  return `/api/v1/accounts/${id}${path}`;
}

async function loadDropdowns() {
  loading.value = true;
  error.value = null;
  try {
    const [proc, dh, ag] = await Promise.all([
      axios.get(buildAccountUrl('/scheduling/procedures')).catch(() => ({ data: [] })),
      axios.get(buildAccountUrl('/digital_humans')).catch(() => ({ data: [] })),
      axios.get(buildAccountUrl('/agents')).catch(() => ({ data: [] })),
    ]);
    procedures.value = Array.isArray(proc.data)
      ? proc.data
      : proc.data?.payload || [];
    digitalHumans.value = Array.isArray(dh.data)
      ? dh.data
      : dh.data?.payload || [];
    physicians.value = Array.isArray(ag.data)
      ? ag.data
      : ag.data?.payload || [];
  } catch (e) {
    error.value = e?.message || 'Erro ao carregar opções';
  } finally {
    loading.value = false;
  }
}

// Mapa de cada lista dinâmica -> seu campo de input. Refs no template são
// "desembrulhados" pelo Vue (viram o valor), então NÃO dá pra passar o ref por
// argumento (vinha string, quebrava). Pegamos o ref real aqui pelo mapa.
const listInputs = {
  previous_diseases: newDisease,
  current_medications: newMedication,
  other_exams: newExam,
  allergies: newAllergy,
};

function addToList(listName) {
  const inputRef = listInputs[listName];
  const value = (inputRef?.value || '').trim();
  if (!value) return;
  if (!Array.isArray(form.value[listName])) form.value[listName] = [];
  form.value[listName] = [...form.value[listName], value];
  inputRef.value = '';
}

function removeFromList(listName, idx) {
  form.value[listName] = form.value[listName].filter((_, i) => i !== idx);
}

function togglePhysician(userId) {
  const ids = form.value.physician_ids || [];
  form.value.physician_ids = ids.includes(userId)
    ? ids.filter(id => id !== userId)
    : [...ids, userId];
}

watch(
  form,
  newVal => {
    emit('change', newVal);
  },
  { deep: true }
);

defineExpose({
  getFormData: () => ({ ...form.value }),
  reset: () => {
    form.value = {
      procedure_id: '',
      assigned_physician_id: '',
      assigned_digital_human_id: '',
      surgery_date: '',
      journey_phase: 'pre_op',
      medical_record_number: '',
      birth_date: '',
      gender: '',
      height_cm: '',
      weight_kg: '',
      blood_type: '',
      allergies: [],
      physician_ids: [],
      last_consultation: '',
      primary_diagnosis: '',
      previous_diseases: [],
      current_medications: [],
      other_exams: [],
      exam_results: '',
      program_name: '',
      subcategory_name: '',
      care_template_name: '',
      care_description: '',
      notes: '',
      medical_guidance: '',
    };
  },
});

onMounted(loadDropdowns);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <section class="flex flex-col gap-6 pt-4 border-t border-n-strong">
    <div class="flex items-center justify-between">
      <h4 class="text-sm font-semibold text-n-slate-12">
        Dados clínicos (opcional)
      </h4>
      <span v-if="loading" class="text-xs text-n-slate-11">carregando…</span>
    </div>

    <div v-if="error" class="text-xs text-n-ruby-11">{{ error }}</div>

    <!-- ============ DADOS DO SISTEMA / VINCULAÇÃO ============ -->
    <div class="flex flex-col gap-3">
      <h5 class="text-xs font-bold text-n-slate-11 uppercase tracking-wider">
        Dados Médicos
      </h5>
      <div class="grid grid-cols-2 gap-3">
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          Procedimento
          <select
            v-model="form.procedure_id"
            class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          >
            <option value="">— selecione —</option>
            <option v-for="p in procedures" :key="p.id" :value="p.id">
              {{ p.name }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          Médico responsável
          <select
            v-model="form.assigned_physician_id"
            class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          >
            <option value="">— selecione —</option>
            <option v-for="u in physicians" :key="u.id" :value="u.id">
              {{ u.name }} ({{ u.email }})
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          Humano Digital
          <select
            v-model="form.assigned_digital_human_id"
            class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          >
            <option value="">— roteamento automático por procedimento —</option>
            <option v-for="h in digitalHumans" :key="h.id" :value="h.id">
              {{ h.name }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          Fase atual
          <select
            v-model="form.journey_phase"
            class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          >
            <option value="pre_op">Pré-op</option>
            <option value="surgery">Cirurgia realizada</option>
            <option value="post_op_phase_1">Pós-op Fase 1</option>
            <option value="post_op_phase_2">Pós-op Fase 2</option>
            <option value="discharged">Alta</option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          Data da cirurgia
          <input
            v-model="form.surgery_date"
            type="date"
            lang="pt-BR"
            class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          />
        </label>

        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          Última consulta
          <input
            v-model="form.last_consultation"
            type="date"
            lang="pt-BR"
            class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          />
        </label>

        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          Data de nascimento
          <input
            v-model="form.birth_date"
            type="date"
            lang="pt-BR"
            class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          />
        </label>

        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          Sexo
          <select
            v-model="form.gender"
            class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          >
            <option value="">—</option>
            <option value="M">Masculino</option>
            <option value="F">Feminino</option>
            <option value="O">Outro</option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-xs text-n-slate-11 col-span-2">
          Número do prontuário (MRN)
          <input
            v-model="form.medical_record_number"
            type="text"
            placeholder="MRN-2026-..."
            class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          />
        </label>
      </div>
    </div>

    <!-- ============ ANTROPOMETRIA E ALERGIAS ============ -->
    <div class="flex flex-col gap-3">
      <h5 class="text-xs font-bold text-n-slate-11 uppercase tracking-wider">
        Antropometria
      </h5>
      <div class="grid grid-cols-2 gap-3">
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          Altura (cm)
          <input
            v-model="form.height_cm"
            type="number"
            step="0.1"
            placeholder="Ex: 175"
            class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          Peso (kg)
          <input
            v-model="form.weight_kg"
            type="number"
            step="0.1"
            placeholder="Ex: 72.5"
            class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          IMC (calculado)
          <input
            :value="bmi !== null ? bmi : '—'"
            type="text"
            readonly
            class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-1 text-sm text-n-slate-11"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          Tipo sanguíneo
          <select
            v-model="form.blood_type"
            class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          >
            <option value="">—</option>
            <option
              v-for="bt in ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']"
              :key="bt"
              :value="bt"
            >
              {{ bt }}
            </option>
          </select>
        </label>
      </div>
    </div>

    <!-- ============ ALERGIAS (lista dinâmica) ============ -->
    <div class="flex flex-col gap-2">
      <h5 class="text-xs font-bold text-n-slate-11 uppercase tracking-wider">
        Alergias
      </h5>
      <div class="flex gap-2">
        <input
          v-model="newAllergy"
          type="text"
          placeholder="Digite uma alergia"
          class="flex-1 px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          @keyup.enter="addToList('allergies')"
        />
        <button
          type="button"
          class="px-4 py-1.5 rounded-md bg-n-brand text-white text-sm font-medium whitespace-nowrap"
          @click="addToList('allergies')"
        >
          adicionar
        </button>
      </div>
      <ul v-if="form.allergies.length > 0" class="flex flex-wrap gap-1.5">
        <li
          v-for="(a, i) in form.allergies"
          :key="i"
          class="flex items-center gap-1 text-xs bg-n-solid-2 border border-n-weak rounded-md px-2 py-1"
        >
          {{ a }}
          <button
            type="button"
            class="text-n-ruby-11 hover:text-n-ruby-12 font-bold"
            @click="removeFromList('allergies', i)"
          >
            ×
          </button>
        </li>
      </ul>
    </div>

    <!-- ============ EQUIPE MÉDICA (multi) ============ -->
    <div v-if="physicians.length > 0" class="flex flex-col gap-2">
      <h5 class="text-xs font-bold text-n-slate-11 uppercase tracking-wider">
        Equipe médica (acesso)
      </h5>
      <p class="text-xs text-n-slate-10">
        Médicos adicionais que podem visualizar este paciente
      </p>
      <div
        class="grid grid-cols-2 gap-1 max-h-32 overflow-auto p-2 rounded-md border border-n-weak bg-n-solid-2"
      >
        <label
          v-for="u in physicians"
          :key="u.id"
          class="flex items-center gap-2 text-xs text-n-slate-12 py-1"
        >
          <input
            type="checkbox"
            :checked="form.physician_ids.includes(u.id)"
            @change="togglePhysician(u.id)"
          />
          {{ u.name }}
        </label>
      </div>
    </div>

    <!-- ============ DIAGNÓSTICO ============ -->
    <label class="flex flex-col gap-1 text-xs text-n-slate-11">
      <h5
        class="text-xs font-bold text-n-slate-11 uppercase tracking-wider mb-1"
      >
        Diagnóstico principal
      </h5>
      <textarea
        v-model="form.primary_diagnosis"
        rows="2"
        class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
        placeholder="Ex: Cisto pilonidal sintomático com episódio recente de abscesso."
      />
    </label>

    <!-- ============ DOENÇAS PRÉVIAS (lista dinâmica) ============ -->
    <div class="flex flex-col gap-2">
      <h5 class="text-xs font-bold text-n-slate-11 uppercase tracking-wider">
        Doenças prévias
      </h5>
      <div class="flex gap-2">
        <input
          v-model="newDisease"
          type="text"
          placeholder="Digite uma doença prévia"
          class="flex-1 px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          @keyup.enter="addToList('previous_diseases')"
        />
        <button
          type="button"
          class="px-4 py-1.5 rounded-md bg-n-brand text-white text-sm font-medium whitespace-nowrap"
          @click="addToList('previous_diseases')"
        >
          adicionar
        </button>
      </div>
      <ul
        v-if="form.previous_diseases.length > 0"
        class="flex flex-wrap gap-1.5"
      >
        <li
          v-for="(d, i) in form.previous_diseases"
          :key="i"
          class="flex items-center gap-1 text-xs bg-n-solid-2 border border-n-weak rounded-md px-2 py-1"
        >
          {{ d }}
          <button
            type="button"
            class="text-n-ruby-11 hover:text-n-ruby-12 font-bold"
            @click="removeFromList('previous_diseases', i)"
          >
            ×
          </button>
        </li>
      </ul>
    </div>

    <!-- ============ MEDICAÇÕES EM USO (lista) ============ -->
    <div class="flex flex-col gap-2">
      <h5 class="text-xs font-bold text-n-slate-11 uppercase tracking-wider">
        Medicações em uso
      </h5>
      <div class="flex gap-2">
        <input
          v-model="newMedication"
          type="text"
          placeholder="Digite uma medicação"
          class="flex-1 px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          @keyup.enter="addToList('current_medications')"
        />
        <button
          type="button"
          class="px-4 py-1.5 rounded-md bg-n-brand text-white text-sm font-medium whitespace-nowrap"
          @click="addToList('current_medications')"
        >
          adicionar
        </button>
      </div>
      <ul
        v-if="form.current_medications.length > 0"
        class="flex flex-wrap gap-1.5"
      >
        <li
          v-for="(m, i) in form.current_medications"
          :key="i"
          class="flex items-center gap-1 text-xs bg-n-solid-2 border border-n-weak rounded-md px-2 py-1"
        >
          {{ m }}
          <button
            type="button"
            class="text-n-ruby-11 hover:text-n-ruby-12 font-bold"
            @click="removeFromList('current_medications', i)"
          >
            ×
          </button>
        </li>
      </ul>
    </div>

    <!-- ============ EXAMES ============ -->
    <div class="flex flex-col gap-2">
      <h5 class="text-xs font-bold text-n-slate-11 uppercase tracking-wider">
        Resultados de exames
      </h5>
      <textarea
        v-model="form.exam_results"
        rows="3"
        class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
        placeholder="Ex.: Colonoscopia em 05/09 sem alterações. Biópsia: inflamação moderada..."
      />
    </div>

    <div class="flex flex-col gap-2">
      <h5 class="text-xs font-bold text-n-slate-11 uppercase tracking-wider">
        Outros exames
      </h5>
      <div class="flex gap-2">
        <input
          v-model="newExam"
          type="text"
          placeholder="Digite um exame realizado"
          class="flex-1 px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          @keyup.enter="addToList('other_exams')"
        />
        <button
          type="button"
          class="px-4 py-1.5 rounded-md bg-n-brand text-white text-sm font-medium whitespace-nowrap"
          @click="addToList('other_exams')"
        >
          adicionar
        </button>
      </div>
      <ul v-if="form.other_exams.length > 0" class="flex flex-wrap gap-1.5">
        <li
          v-for="(e, i) in form.other_exams"
          :key="i"
          class="flex items-center gap-1 text-xs bg-n-solid-2 border border-n-weak rounded-md px-2 py-1"
        >
          {{ e }}
          <button
            type="button"
            class="text-n-ruby-11 hover:text-n-ruby-12 font-bold"
            @click="removeFromList('other_exams', i)"
          >
            ×
          </button>
        </li>
      </ul>
    </div>

    <!-- ============ ACOMPANHAMENTO E ORIENTAÇÕES ============ -->
    <div class="flex flex-col gap-3">
      <h5 class="text-xs font-bold text-n-slate-11 uppercase tracking-wider">
        Acompanhamento e orientações
      </h5>

      <div class="grid grid-cols-2 gap-3">
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          Categoria / Programa
          <input
            v-model="form.program_name"
            type="text"
            placeholder="Ex: Pós-op coloproctologia"
            class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          Subcategoria
          <input
            v-model="form.subcategory_name"
            type="text"
            placeholder="Ex: EPSiT"
            class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          />
        </label>
      </div>

      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        Modelo de cuidado
        <input
          v-model="form.care_template_name"
          type="text"
          placeholder="Ex: Template padrão EPSiT (6 meses)"
          class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
        />
      </label>

      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        Descrição do acompanhamento
        <textarea
          v-model="form.care_description"
          rows="2"
          class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          placeholder="Descreva o plano de envio para este paciente..."
        />
      </label>

      <div class="grid grid-cols-2 gap-3">
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          Observações gerais
          <textarea
            v-model="form.notes"
            rows="3"
            class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
            placeholder="Observações importantes sobre o paciente..."
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          Orientações médicas
          <textarea
            v-model="form.medical_guidance"
            rows="3"
            class="px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
            placeholder="Orientações específicas para o paciente..."
          />
        </label>
      </div>
    </div>
  </section>
</template>
